import PostStatusForm from '../post_status_form/post_status_form.vue'
import UserAvatar from '../user_avatar/user_avatar.vue'
import Attachment from '../attachment/attachment.vue'
import statusPoster from '../../services/status_poster/status_poster.service.js'

const DMConvTimeline = {
  components: {
    PostStatusForm,
    UserAvatar,
    Attachment
  },

  data () {
    return {
      conversationId: null
    }
  },

  computed: {
    conversation () {
      return this.$store.getters.getDMConversationById(this.conversationId)
    },

    timeline () {
      return this.$store.state.statuses.timelines.dmConv
    },

    messages () {
      const statuses = this.timeline?.visibleStatuses || []

      return [...statuses].sort((a, b) => {
        return new Date(a.created_at) - new Date(b.created_at)
      })
    },

    currentUser () {
      return this.$store.state.users.currentUser
    },

    currentUserId () {
      return String(this.currentUser?.id || '')
    },

    recipients () {
      const accounts = this.conversation?.accounts || []

      const others = accounts.filter(user => {
        return String(user.id) !== this.currentUserId
      })

      return others.length ? others : accounts
    },

    primaryRecipient () {
      return this.recipients[0] || null
    },

    conversationName () {
      if (!this.primaryRecipient) return 'Direct Message'

      if (this.recipients.length > 1) {
        const names = this.recipients
          .slice(0, 3)
          .map(user => user.name || user.screen_name || user.nickname)
          .filter(Boolean)

        return names.join(', ') + (this.recipients.length > 3 ? '…' : '')
      }

      return this.primaryRecipient.name ||
        this.primaryRecipient.screen_name ||
        this.primaryRecipient.nickname ||
        'Direct Message'
    },

    recipientHandle () {
      if (!this.primaryRecipient || this.recipients.length !== 1) return ''

      const handle =
        this.primaryRecipient.screen_name ||
        this.primaryRecipient.nickname ||
        this.primaryRecipient.acct ||
        ''

      if (!handle) return ''

      return handle.startsWith('@') ? handle : `@${handle}`
    },

    composerAttentions () {
      return this.recipients
    },

    composerPlaceholder () {
      return `Message ${this.conversationName}…`
    }
  },

  methods: {
    isMine (status) {
      return String(status?.user?.id || '') === this.currentUserId
    },

    senderName (status) {
      if (this.isMine(status)) return 'You'

      const user = status?.user
      if (!user) return ''

      return user.name ||
        user.screen_name ||
        user.nickname ||
        'User'
    },

    messageText (status) {
      if (!status) return ''

      let text = status.text || ''

      // Direct statuses commonly include recipient mentions in the
      // underlying status body. They are routing information here,
      // not useful chat content.
      const recipients = this.recipients || []

      recipients.forEach(user => {
        const possible = [
          user.screen_name,
          user.nickname,
          user.acct
        ].filter(Boolean)

        possible.forEach(handle => {
          const clean = String(handle).replace(/^@/, '')
          const escaped = clean.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

          text = text.replace(
            new RegExp(`^\\s*@${escaped}\\s*`, 'i'),
            ''
          )
        })
      })

      return text.trim()
    },

    messageTime (status) {
      if (!status?.created_at) return ''

      const date = new Date(status.created_at)

      return date.toLocaleTimeString([], {
        hour: 'numeric',
        minute: '2-digit'
      })
    },

    messageDate (status) {
      if (!status?.created_at) return ''

      const date = new Date(status.created_at)

      return date.toLocaleDateString([], {
        month: 'short',
        day: 'numeric',
        year: date.getFullYear() !== new Date().getFullYear()
          ? 'numeric'
          : undefined
      })
    },

    sameDayAsPrevious (index) {
      if (index === 0) return false

      const current = this.messages[index]
      const previous = this.messages[index - 1]

      if (!current?.created_at || !previous?.created_at) return false

      return new Date(current.created_at).toDateString() ===
        new Date(previous.created_at).toDateString()
    },

    scrollToBottom () {
      this.$nextTick(() => {
        const el = this.$refs.messageList
        if (!el) return

        el.scrollTop = el.scrollHeight
      })
    },

    postDMStatus (options) {
      const messages = this.messages || []

      const newestMessage = messages.length
        ? messages[messages.length - 1]
        : null

      const replyAnchor =
        newestMessage?.id ||
        this.conversation?.last_status?.id ||
        undefined

      return statusPoster.postStatus({
        ...options,
        visibility: 'direct',
        inReplyToStatusId: replyAnchor
      })
    },

    handlePosted (data) {
      if (data && !data.error) {
        const status = data.status || data

        this.$store.dispatch('addNewStatuses', {
          statuses: [status],
          timeline: 'dmConv',
          showImmediately: true,
          noIdUpdate: true
        })

        this.$store.dispatch('fetchDMConversationDetails', {
          id: this.conversationId
        })
      }

      this.scrollToBottom()
    },

    goBack () {
      this.$router.push({ name: 'dms' })
    }
  },

  created () {
    this.conversationId = this.$route.params.id

    this.$store.dispatch('fetchDMConversationDetails', {
      id: this.conversationId
    })

    this.$store.dispatch('startFetchingTimeline', {
      timeline: 'dmConv',
      conversationId: this.conversationId
    })

    this.$store.dispatch('markDMConversationAsRead', {
      id: this.conversationId
    })
  },

  mounted () {
    setTimeout(() => {
      this.scrollToBottom()
    }, 700)
  },

  updated () {
    this.scrollToBottom()
  },

  unmounted () {
    this.$store.dispatch('stopFetchingTimeline', 'dmConv')
    this.$store.commit('clearTimeline', { timeline: 'dmConv' })
  }
}

export default DMConvTimeline
